import { useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import CoinChart from '../../components/chart/CoinChart';
import TrendPercentage from '../../components/chart/TrendPercentage';

export default function CompareDashboard() {
    const { search } = useLocation();
    const navigate = useNavigate();
    const params = new URLSearchParams(search);

    const [selectedCoin1, setSelectedCoin1] = useState(params.get('coin1') || 'bitcoin');
    const [selectedCoin2, setSelectedCoin2] = useState(params.get('coin2') || 'ethereum');

    const [coinData, setCoinData] = useState({
        coin1: { predictions: {}, last_30_days: {}, uptrend_percentage: 0, downtrend_percentage: 0 },
        coin2: { predictions: {}, last_30_days: {}, uptrend_percentage: 0, downtrend_percentage: 0 }
    });

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [chatVisible, setChatVisible] = useState(false);
    const [chatInput, setChatInput] = useState('');
    const [chatMessages, setChatMessages] = useState([
        { sender: 'bot', text: 'Hello! How can we help you today?' }
    ]);

    const messagesEndRef = useRef(null);

    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [chatMessages]);

    const formatChartData = (data, isPredictions = false) => {
        if (!data) return [];
        return Object.entries(data).map(([time, price]) => ({
            time,
            price,
            isPrediction: isPredictions
        }));
    };

    const getCoin1ChartData = () => {
        const historicalData = formatChartData(coinData.coin1.last_30_days);
        const predictionData = formatChartData(coinData.coin1.predictions, true);
        return [...historicalData.slice(-7), ...predictionData];
    };

    const getCoin2ChartData = () => {
        const historicalData = formatChartData(coinData.coin2.last_30_days);
        const predictionData = formatChartData(coinData.coin2.predictions, true);
        return [...historicalData.slice(-7), ...predictionData];
    };

    const fetchCryptoData = async (coin1, coin2) => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await fetch('https://crypto-co.nugenesisou.com/predict', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'accept': 'application/json'
                },
                body: JSON.stringify({ coin1, coin2 })
            });

            if (!response.ok) {
                throw new Error(`API request failed with status ${response.status}`);
            }

            const data = await response.json();
            setCoinData(data);
            navigate(`?coin1=${coin1}&coin2=${coin2}`, { replace: true });
        } catch (err) {
            console.error('Error fetching crypto data:', err);
            setError(err.message || 'Failed to fetch data');
        } finally {
            setIsLoading(false);
        }
    };

    const handleCompare = (e) => {
        e.preventDefault();
        fetchCryptoData(selectedCoin1, selectedCoin2);
    };

    const sendMessageToChatbot = async (message) => {
        if (!message.trim()) return;

        // Add user message and typing bot message
        setChatMessages(prev => [...prev, { sender: 'user', text: message }, { sender: 'bot', text: 'Typing...' }]);
        setChatInput('');

        try {
            const response = await fetch('https://crypto-co.nugenesisou.com/chat', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'accept': 'application/json'
                },
                body: JSON.stringify({
                    coin1: selectedCoin1,
                    coin2: selectedCoin2,
                    historical_data: true,
                    prediction_data: true,
                    message: message
                })
            });

            const data = await response.json();

            setChatMessages(prev => {
                const updated = [...prev];
                updated[updated.length - 1] = { sender: 'bot', text: data.response || 'No response received' };
                return updated;
            });
        } catch (error) {
            console.error('Chatbot error:', error);
            setChatMessages(prev => {
                const updated = [...prev];
                updated[updated.length - 1] = { sender: 'bot', text: 'Oops! Something went wrong.' };
                return updated;
            });
        }
    };

    return (
        <>
            <Header />
            <main>
                <section className="pad breadcrumbs top-cover">
                    <div className="content">
                        <a href="/">Home</a><i></i>
                        <span>Crypto Compare</span>
                    </div>
                </section>

                <section className="security-hero not-min-height bottom0">
                    <div className="content">
                        <div className="pad texts full-width">
                            <h1><b>Crypto Compare Dashboard</b></h1>
                            <div className="text">
                                Track real-time prices, 7-day trend percentages, market capitalization, volume shifts, and AI-generated predictions all in a single, easy-to-navigate dashboard.
                            </div>
                        </div>
                    </div>
                </section>

                <section className="pad security-hero not-min-height marign-low-bottom-25">
                    <div className="content">
                        <form className="compare-form" onSubmit={handleCompare}>
                            <div className="controls">
                                <select value={selectedCoin1} onChange={(e) => setSelectedCoin1(e.target.value)}>
                                    <option value="bitcoin">Bitcoin</option>
                                    <option value="ethereum">Ethereum</option>
                                    <option value="cardano">ADA (Cardano)</option>
                                    <option value="solana">Solana</option>
                                    <option value="Dogecoin">Dogecoin (DOGE)</option>
                                    <option value="Tron">TRON (TRX)</option>
                                    <option value="Litecoin">Litecoin</option>
                                    <option value="Decentraland">MANA</option>
                                    <option value="Shiba">Shiba Inu (SHIB)</option>
                                </select>
                                <h1><b>VS</b></h1>
                                <select value={selectedCoin2} onChange={(e) => setSelectedCoin2(e.target.value)}>
                                    <option value="ethereum">Ethereum</option>
                                    <option value="bitcoin">Bitcoin</option>
                                    <option value="cardano">ADA (Cardano)</option>
                                    <option value="solana">Solana</option>
                                    <option value="Dogecoin">Dogecoin (DOGE)</option>
                                    <option value="Tron">TRON (TRX)</option>
                                    <option value="Litecoin">Litecoin</option>
                                    <option value="Decentraland">MANA</option>
                                    <option value="Shiba">Shiba Inu (SHIB)</option>
                                </select>
                                <button type="submit" disabled={isLoading}>
                                    {isLoading ? 'Loading...' : 'Compare'}
                                </button>
                                <button type="button" className="support-btn" onClick={() => setChatVisible(true)}>
                                Talk to Navis
                                </button>
                            </div>
                        </form>
                    </div>
                </section>

                {error && (
                    <section className="pad security-hero">
                        <div className="content">
                            <div className="error-message">
                                <p>Error: {error}</p>
                                <p>Please try again later or check your connection.</p>
                            </div>
                        </div>
                    </section>
                )}

                <section className="pad security-hero margin-top-content5 compare-dashboard-section">
                    <div className="content">
                        <div className="compare-dashboard">
                            <div className="coin1">
                                <h1>{selectedCoin1}</h1>
                                <div className="coin-chart-Prediction">
                                    <p>7-Day Crypto Market Prediction</p>
                                    <CoinChart data={getCoin1ChartData()} />
                                </div>
                                <div className="coin-chart-trend margin-top-content5">
                                    <p>Crypto Forecast with Trend Percentages</p>
                                    <TrendPercentage up={coinData.coin1.uptrend_percentage} down={coinData.coin1.downtrend_percentage} />
                                </div>
                                {coinData.coin1.current_price && (
                                    <div className="price-info margin-top-content5">
                                        <p>Current Price: ${coinData.coin1.current_price.toLocaleString()}</p>
                                        <p>Tomorrow's Prediction: ${coinData.coin1.tomorrow_price?.toLocaleString()}</p>
                                    </div>
                                )}
                            </div>

                            <div className="coin2">
                                <h1>{selectedCoin2}</h1>
                                <div className="coin-chart-Prediction">
                                    <p>7-Day Crypto Market Prediction</p>
                                    <CoinChart data={getCoin2ChartData()} />
                                </div>
                                <div className="coin-chart-trend margin-top-content5">
                                    <p>Crypto Forecast with Trend Percentages</p>
                                    <TrendPercentage up={coinData.coin2.uptrend_percentage} down={coinData.coin2.downtrend_percentage} />
                                </div>
                                {coinData.coin2.current_price && (
                                    <div className="price-info margin-top-content5">
                                        <p>Current Price: ${coinData.coin2.current_price.toLocaleString()}</p>
                                        <p>Tomorrow's Prediction: ${coinData.coin2.tomorrow_price?.toLocaleString()}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </section>
            </main>
            <Footer />

            {chatVisible && (
                <div className="compare-chatbot-popup">
                    <div className="compare-chatbot-header">
                        <span>Navis</span>
                        <button onClick={() => setChatVisible(false)}>✖</button>
                    </div>
                    <div className="compare-chatbot-body">
                        <div className="messages" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                            {chatMessages.map((msg, idx) => (
                                <p key={idx} className={msg.sender === 'user' ? 'compare-chat-user' : 'compare-chat-bot'}>
                                    <strong>{msg.sender === 'user' ? 'You' : 'Bot'}:</strong> {msg.text}
                                </p>
                            ))}
                            <div ref={messagesEndRef} />
                        </div>
                        <form
                            className="compare-chat-input"
                            onSubmit={(e) => {
                                e.preventDefault();
                                sendMessageToChatbot(chatInput);
                            }}
                        >
                            <input
                                type="text"
                                placeholder="Type your message..."
                                value={chatInput}
                                onChange={(e) => setChatInput(e.target.value)}
                            />
                            <button type="submit" id="sendmsg">Send</button>
                        </form>
                    </div>
                </div>
            )}
        </>
    );
}
